import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { UserInfoContext } from '../context/UserInfoProvider'
import { getFormattedDate, getUserGeofence } from '../services/apiUserService'
import {
  fetchActiveStoreRelationsForUser,
  reassignActiveStoreRelationsForUser
} from '../services/storeRelationDeactivationService'
import ResetPasswordModal from '../component/modal/ResetPasswordModal'
import Select from 'react-select'
import { canManageUsersApp } from '../../global_context/capabilities/userAppCapabilities'
import { canManagePocStoreRelations } from '../../global_context/capabilities/pocCapabilities'
import DeactivateUserModal from '../component/modal/DeactivateUserModal'
import ConfirmRoleRemovalModal from '../component/modal/ConfirmRoleRemovalModal'
import ReassignUserModal from '../../poc/StoreRelations/components/modal/ReassignUserModal'
import useStoreRelationSalesmanSearch from '../../poc/StoreRelations/hooks/useStoreRelationSalesmanSearch'
import UserHeader from '../../components/UserHeader/UserHeader'
import DetailsSummarySection from '../../components/details/summary/DetailsSummarySection'
import GoogleIcon from '../../components/icon/GoogleIcon'
import ActionButton from '../../components/action_button/ActionButton'
import {
  UI_COLOR_ACCENT_ORANGE_ACTION,
  UI_COLOR_ACCENT_ORANGE_ACTION_HOVER,
  UI_COLOR_BORDER_MUTED,
  UI_COLOR_BUTTON_MUTED,
  UI_COLOR_BUTTON_MUTED_TEXT,
  UI_COLOR_CHIP_INFO_BG,
  UI_COLOR_CHIP_INFO_TEXT,
  UI_COLOR_PRIMARY_ALT,
  UI_COLOR_SURFACE_READONLY,
  UI_COLOR_TEXT_BODY_DARK,
  UI_COLOR_TEXT_NEUTRAL
} from '../../constants/uiColors'
import { UI_TEXT_EMPTY_VALUE, UI_TEXT_NO, UI_TEXT_YES } from '../../constants/uiText'

const USER_HEADER_CONFIG = {
  titleKey: 'full_name',
  cards: [
    {
      icon: 'barcode_scanner',
      valueKey: 'route_db_ref',
      filled: true
    },
    {
      icon: 'mail',
      valueKey: 'email',
      filled: true
    },
    {
      icon: 'phone',
      valueKey: 'mobile',
      filled: true
    }
  ]
}

const USER_ROLE_CHIP_SX = {
  height: 20,
  borderRadius: 2,
  backgroundColor: UI_COLOR_CHIP_INFO_BG,
  color: UI_COLOR_CHIP_INFO_TEXT,
  fontSize: 10,
  fontWeight: 500,
  '& .MuiChip-label': {
    fontWeight: 500,
    px: 1
  }
}

const GEOFENCE_STATUS_CHIP_SX = {
  height: 24,
  borderRadius: 999,
  backgroundColor: UI_COLOR_BORDER_MUTED,
  color: UI_COLOR_TEXT_NEUTRAL,
  fontSize: 12,
  fontWeight: 500,
  '& .MuiChip-label': {
    px: 1.25
  }
}

const formatUserDate = (date) => (date ? getFormattedDate(date) : UI_TEXT_EMPTY_VALUE)

const UserInfoDetailTab = () => {
  const {
    userInfoData,
    isEditingUserInfoTab,
    isFetching,
    handleUpdateUser,
    handleSubmit,
    errors,
    register,
    onSubmit,
    Controller,
    control,
    featureRoles,
    warehouses,
    getRoleLabel
  } = useContext(UserInfoContext)
  const canManagePocModules = canManagePocStoreRelations()

  const [showModal, setShowModal] = useState(false)
  const [showModalUsuario, setShowModalUsuario] = useState(false)
  const [showModalRole, setShowModalRole] = useState(false)
  const [activeStatus, setActiveStatus] = useState(userInfoData.active)
  const [currentRoles, setCurrentRoles] = useState(
    userInfoData.roles?.map((role) => role.feature_role_key) || []
  )
  const [currentWarehouse, setCurrentWarehouse] = useState(userInfoData.warehouse_uid)
  const [confirmedRoleRemoval, setConfirmedRoleRemoval] = useState(false)
  const [geofenceSummary, setGeofenceSummary] = useState(null)
  const [storeRelationAlert, setStoreRelationAlert] = useState(null)
  const [checkingStoreRelations, setCheckingStoreRelations] = useState(false)
  const [reassigningStoreRelations, setReassigningStoreRelations] = useState(false)
  const [reassignModalState, setReassignModalState] = useState({
    open: false,
    activeRelations: [],
    nextUser: null,
    nextUserSearch: ''
  })
  const pendingSubmissionRef = useRef(null)

  useEffect(() => {
    setActiveStatus(userInfoData.active)
    setCurrentRoles(userInfoData.roles?.map((role) => role.feature_role_key) || [])
    setCurrentWarehouse(userInfoData.warehouse_uid)
  }, [userInfoData.active, userInfoData.roles, userInfoData.warehouse_uid])

  useEffect(() => {
    let isMounted = true

    const fetchGeofenceSummary = async () => {
      if (!userInfoData?.id) return

      try {
        const response = await getUserGeofence(userInfoData.id)
        if (!response?.ok) return
        const data = await response.json()

        if (isMounted) {
          setGeofenceSummary(data)
        }
      } catch (error) {
        console.error('fetchGeofenceSummary error', error)
      }
    }

    fetchGeofenceSummary()

    return () => {
      isMounted = false
    }
  }, [userInfoData?.id])

  const showResetPasswordModal = () => {
    setShowModal(true)
  }

  const closeMainModal = () => {
    setShowModal(false)
  }

  const showDeactivateUserModal = useCallback(() => {
    setShowModalUsuario(true)
  }, [])

  const submitPendingDeactivation = useCallback(() => {
    const pendingSubmission = pendingSubmissionRef.current

    pendingSubmissionRef.current = null

    if (!pendingSubmission) return

    setConfirmedRoleRemoval(false)
    onSubmit(pendingSubmission)
  }, [onSubmit])

  const closeDeactivateUserModal = async (confirmed) => {
    setShowModalUsuario(false)

    if (confirmed) {
      await continueDeactivateAfterConfirmation()
      return
    }

    pendingSubmissionRef.current = null
  }

  const showRoleRemovalModal = () => {
    setShowModalRole(true)
  }

  const closeRoleRemovalModal = (confirmed) => {
    if (confirmed) {
      setConfirmedRoleRemoval(true)
    }
    setShowModalRole(false)
  }

  const handleStatusChange = (event) => {
    const value = event.target.value === 'true'
    setActiveStatus(value)
  }

  const handleRoleChange = (selectedOptions) => {
    setCurrentRoles(selectedOptions ? selectedOptions.map((option) => option.value) : [])
  }

  const handleWarehouseChange = (selectedOptions) => {
    setCurrentWarehouse(selectedOptions ? selectedOptions.value : null)
  }

  const clearStoreRelationAlert = useCallback(() => {
    setStoreRelationAlert(null)
  }, [])

  const closeReassignUserModal = useCallback(() => {
    setReassignModalState({
      open: false,
      activeRelations: [],
      nextUser: null,
      nextUserSearch: ''
    })
    setReassigningStoreRelations(false)
    pendingSubmissionRef.current = null
  }, [])

  const currentUserOption = useMemo(() => {
    const currentUserDbRef = userInfoData?.route_db_ref?.trim() ?? null
    const currentUserName = userInfoData?.full_name?.trim() ?? null

    return {
      id: userInfoData?.id,
      db_ref: currentUserDbRef,
      name: currentUserName,
      label:
        [currentUserDbRef, currentUserName].filter(Boolean).join(' - ') ||
        userInfoData?.email ||
        'Usuario actual'
    }
  }, [userInfoData?.email, userInfoData?.full_name, userInfoData?.id, userInfoData?.route_db_ref])

  const reassignDescription = useMemo(() => {
    const activeRelationsCount = reassignModalState.activeRelations.length

    if (activeRelationsCount === 1) {
      return 'Este usuario tiene 1 relacion activa. Debes reasignarla antes de continuar con la desactivacion.'
    }

    return `Este usuario tiene ${activeRelationsCount} relaciones activas. Debes reasignarlas antes de continuar con la desactivacion.`
  }, [reassignModalState.activeRelations.length])

  const { options: searchedReassignUserOptions, loading: reassignUsersLoading } =
    useStoreRelationSalesmanSearch(reassignModalState.nextUserSearch)

  const reassignUserOptions = useMemo(() => {
    const seenKeys = new Set()

    return searchedReassignUserOptions.filter((option) => {
      const optionDbRef = option?.db_ref?.trim() ?? null

      if (!optionDbRef) return false
      if (optionDbRef === currentUserOption.db_ref) return false
      if (seenKeys.has(optionDbRef)) return false

      seenKeys.add(optionDbRef)
      return true
    })
  }, [currentUserOption.db_ref, searchedReassignUserOptions])

  const openReassignUserModal = useCallback((activeRelations) => {
    setReassignModalState({
      open: true,
      activeRelations,
      nextUser: null,
      nextUserSearch: ''
    })
  }, [])

  const continueDeactivateAfterConfirmation = useCallback(async () => {
    const currentUserDbRef = userInfoData?.route_db_ref?.trim()

    if (!pendingSubmissionRef.current) return

    if (!canManagePocModules) {
      submitPendingDeactivation()
      return
    }

    if (!currentUserDbRef) {
      submitPendingDeactivation()
      return
    }

    setCheckingStoreRelations(true)
    clearStoreRelationAlert()

    try {
      const activeRelations = await fetchActiveStoreRelationsForUser(currentUserDbRef)

      if (!activeRelations.length) {
        submitPendingDeactivation()
        return
      }

      openReassignUserModal(activeRelations)
    } catch (error) {
      console.error('continueDeactivateAfterConfirmation error', error)
      pendingSubmissionRef.current = null
      setStoreRelationAlert({
        type: 'danger',
        message:
          error?.message ||
          'No fue posible validar las relaciones activas del usuario. Intenta nuevamente.'
      })
    } finally {
      setCheckingStoreRelations(false)
    }
  }, [
    canManagePocModules,
    clearStoreRelationAlert,
    openReassignUserModal,
    submitPendingDeactivation,
    userInfoData?.route_db_ref
  ])

  const handleConfirmReassignUser = useCallback(async () => {
    const currentUserDbRef = userInfoData?.route_db_ref?.trim()
    const pendingSubmission = pendingSubmissionRef.current

    if (!pendingSubmission) {
      return
    }

    if (!currentUserDbRef) {
      setStoreRelationAlert({
        type: 'danger',
        message:
          'El usuario actual no tiene codigo de rutero. No es posible reasignar relaciones activas.'
      })
      return
    }

    if (!reassignModalState.nextUser?.db_ref?.trim()) {
      setStoreRelationAlert({
        type: 'danger',
        message: 'Selecciona un usuario destino valido para continuar.'
      })
      return
    }

    setReassigningStoreRelations(true)
    clearStoreRelationAlert()

    try {
      await reassignActiveStoreRelationsForUser({
        currentSalesmanDbRef: currentUserDbRef,
        nextSalesman: reassignModalState.nextUser,
        activeRelations: reassignModalState.activeRelations
      })

      setReassignModalState({
        open: false,
        activeRelations: [],
        nextUser: null,
        nextUserSearch: ''
      })
      submitPendingDeactivation()
    } catch (error) {
      console.error('handleConfirmReassignUser error', error)
      setStoreRelationAlert({
        type: 'danger',
        message: error?.message || 'No fue posible reasignar las relaciones activas del usuario.'
      })
    } finally {
      setReassigningStoreRelations(false)
    }
  }, [
    clearStoreRelationAlert,
    reassignModalState.activeRelations,
    reassignModalState.nextUser,
    submitPendingDeactivation,
    userInfoData?.route_db_ref
  ])

  const handleSubmitForm = async (data) => {
    const rolesRemoved = userInfoData.roles?.some(
      (role) => !currentRoles.includes(role.feature_role_key)
    )

    if (userInfoData.active && !activeStatus) {
      pendingSubmissionRef.current = data
      showDeactivateUserModal()
      return
    } else if (rolesRemoved && !confirmedRoleRemoval && !(userInfoData.active && !activeStatus)) {
      showRoleRemovalModal()
    } else {
      pendingSubmissionRef.current = null
      setConfirmedRoleRemoval(false)
      onSubmit(data)
    }
  }

  const isRoleSupervisor = userInfoData?.roles?.[0]?.feature_role_key === 'role.supervisor'
  const canWriteUser = canManageUsersApp()

  const userRoleLabels = useMemo(
    () =>
      userInfoData?.roles
        ?.filter((role) => role.feature_role_key !== 'role.supervisor')
        .map((role) => getRoleLabel(role.feature_role_key))
        .filter(Boolean) || [],
    [getRoleLabel, userInfoData?.roles]
  )

  const userSummaryFields = useMemo(() => {
    const roleValue = userRoleLabels.length ? (
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
        {userRoleLabels.map((roleLabel, index) => (
          <Chip
            key={`${roleLabel}-${index}`}
            label={roleLabel}
            size="small"
            sx={USER_ROLE_CHIP_SX}
          />
        ))}
      </Stack>
    ) : (
      UI_TEXT_EMPTY_VALUE
    )

    const activeGeofenceDistance =
      geofenceSummary?.active_geo_max_distance_in_meter ??
      geofenceSummary?.business_geo_max_distance_in_meter ??
      100
    const isGlobalGeofence = geofenceSummary?.source !== 'user'
    const geofenceValue = geofenceSummary ? (
      <Stack direction="row" spacing={1.25} alignItems="center" useFlexGap flexWrap="wrap">
        <Typography sx={{ fontSize: 14, color: UI_COLOR_TEXT_BODY_DARK }}>
          {`${activeGeofenceDistance} m`}
        </Typography>
        <Chip
          label={isGlobalGeofence ? 'Valor global del sistema' : 'Geocerca personalizada'}
          size="small"
          sx={GEOFENCE_STATUS_CHIP_SX}
        />
      </Stack>
    ) : (
      UI_TEXT_EMPTY_VALUE
    )

    return [
      {
        label: 'ROL DE USUARIO ACTIVOS',
        value: roleValue
      },
      {
        label: 'ROL DE SUPERVISOR',
        value: isRoleSupervisor ? UI_TEXT_YES : UI_TEXT_NO
      },
      {
        label: 'CÓDIGO DE BODEGA',
        value: userInfoData?.warehouse_uid || UI_TEXT_EMPTY_VALUE
      },
      {
        label: 'GEOCERCA ACTIVA',
        value: geofenceValue
      },
      {
        label: 'CREADO',
        value: formatUserDate(userInfoData?.created_at)
      },
      {
        label: 'ACTUALIZADO',
        value: formatUserDate(userInfoData?.updated_at)
      }
    ]
  }, [
    geofenceSummary,
    isRoleSupervisor,
    userInfoData?.created_at,
    userInfoData?.updated_at,
    userInfoData?.warehouse_uid,
    userRoleLabels
  ])

  const userHeaderStatus = {
    label: userInfoData?.active ? 'ACTIVO' : 'INACTIVO',
    active: Boolean(userInfoData?.active)
  }

  const userDetailActions = canWriteUser ? (
    <Box
      className="show-buttons-responsive"
      sx={{
        justifyContent: { xs: 'stretch', sm: 'flex-end' },
        flexWrap: 'wrap'
      }}
    >
      <ActionButton
        variant="outlined"
        text="Cambiar contraseña"
        icon="lock"
        color={UI_COLOR_BUTTON_MUTED}
        textColor={UI_COLOR_BUTTON_MUTED_TEXT}
        onClick={showResetPasswordModal}
      />
      <ActionButton
        variant="contained"
        text="Actualizar detalles"
        icon="edit"
        filled
        color={UI_COLOR_ACCENT_ORANGE_ACTION}
        hoverColor={UI_COLOR_ACCENT_ORANGE_ACTION_HOVER}
        onClick={() => handleUpdateUser({ target: { value: 'Actualizar detalles' } })}
      />
    </Box>
  ) : null

  return (
    <>
      <div>
        {storeRelationAlert && (
          <div
            className={`alert alert-${storeRelationAlert.type} d-flex justify-content-between align-items-center`}
          >
            <strong className="mr-10">{storeRelationAlert.message}</strong>
            <button type="button" className="btn-close" onClick={clearStoreRelationAlert} />
          </div>
        )}
        {showModal && (
          <ResetPasswordModal
            user={userInfoData}
            showModal={true}
            closeMainModal={closeMainModal}
          />
        )}
        {showModalUsuario && (
          <DeactivateUserModal
            user={userInfoData}
            showModal={true}
            closeModal={closeDeactivateUserModal}
          />
        )}
        {showModalRole && (
          <ConfirmRoleRemovalModal showModal={true} closeModal={closeRoleRemovalModal} />
        )}
        {reassignModalState.open && (
          <ReassignUserModal
            show={reassignModalState.open}
            description={reassignDescription}
            currentUser={currentUserOption}
            nextUser={reassignModalState.nextUser}
            nextUserOptions={reassignUserOptions}
            nextUserSearch={reassignModalState.nextUserSearch}
            loading={reassignUsersLoading}
            confirming={reassigningStoreRelations}
            confirmLabel="Reasignar y continuar"
            onClose={closeReassignUserModal}
            onConfirm={handleConfirmReassignUser}
            onNextUserChange={(nextUser) => {
              setReassignModalState((prev) => ({
                ...prev,
                nextUser,
                nextUserSearch: nextUser?.label ?? prev.nextUserSearch
              }))
            }}
            onNextUserSearchChange={(nextUserSearch) => {
              setReassignModalState((prev) => ({
                ...prev,
                nextUserSearch,
                nextUser: null
              }))
            }}
          />
        )}
        {isEditingUserInfoTab === false && (
          <Box sx={{ pt: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
                borderBottom: `1px solid ${UI_COLOR_BORDER_MUTED}`,
                pb: 2,
                mb: 2
              }}
            >
              <Typography sx={{ fontSize: 18 }}>Detalles del usuario:</Typography>
              {userDetailActions}
            </Box>

            <UserHeader data={userInfoData} config={USER_HEADER_CONFIG} status={userHeaderStatus} />

            <DetailsSummarySection
              summaryFields={userSummaryFields}
              summaryTitle="DETALLES DEL USUARIO"
              summaryOpenLabel="Abrir detalles del usuario"
              summaryCloseLabel="Cerrar detalles del usuario"
              summaryIcon={
                <GoogleIcon name="account_box" size={20} color={UI_COLOR_PRIMARY_ALT} filled />
              }
            />
          </Box>
        )}
        {isEditingUserInfoTab === true && (
          <div className="">
            <div className="show-area-tab">
              <div className="">
                <form onSubmit={handleSubmit(handleSubmitForm)}>
                  <div className="inline-block full-width">
                    <div className="float-left">
                      <h4 className="text-sub-title">Detalles de Usuario:</h4>
                    </div>
                    <div className="float-right">
                      <button
                        className="shadow-cancel-button"
                        value="Cancelar"
                        onClick={handleUpdateUser}
                        disabled={checkingStoreRelations || reassigningStoreRelations}
                      >
                        {' '}
                        Cancelar
                      </button>
                      <input
                        className="save-button"
                        value={
                          checkingStoreRelations || reassigningStoreRelations
                            ? 'Procesando...'
                            : 'Guardar'
                        }
                        type="submit"
                        disabled={checkingStoreRelations || reassigningStoreRelations}
                      />
                    </div>
                  </div>
                  <hr />
                  <p className="mb-30 mt-30" style={{ color: 'red' }}>
                    Campos obligatorios*
                  </p>

                  <div className="form-group row">
                    <label className="col-sm-3 col-form-label">Activo:</label>
                    <div className="col-sm-8">
                      <select
                        name="active"
                        {...register('active')}
                        className={`form-control ${errors.active ? 'is-invalid' : ''}`}
                        value={activeStatus}
                        onChange={handleStatusChange}
                      >
                        <option value="true">Sí</option>
                        <option value="false">No</option>
                      </select>
                      <div className="invalid-feedback">{errors.active?.message}</div>
                    </div>
                  </div>

                  <div className="form-group row show-data-form">
                    <label htmlFor="inputName" className="col-sm-3 col-form-label">
                      Nombre de usuario: <span className="red">*</span>
                    </label>
                    <div className="col-sm-8">
                      <input
                        name="name"
                        type="text"
                        {...register('name')}
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      />
                      <div className="invalid-feedback">{errors.name?.message}</div>
                    </div>
                  </div>

                  <div className="form-group row show-data-form">
                    <label htmlFor="inputName" className="col-sm-3 col-form-label">
                      Rol de supervisor:
                    </label>
                    <div className="col-sm-8 flex-align-center">
                      <span className="supervisor-radio-span">
                        <input
                          type="radio"
                          className="supervisor-radio-option"
                          checked={isRoleSupervisor}
                          disabled
                        />
                        Si
                      </span>
                      <span className="supervisor-radio-span">
                        <input
                          type="radio"
                          className="supervisor-radio-option"
                          checked={!isRoleSupervisor}
                          disabled
                        />
                        No
                      </span>
                    </div>
                  </div>

                  <div className="form-group row show-data-form">
                    <label htmlFor="inputCode" className="col-sm-3 col-form-label">
                      Rol de usuario: <span className="red">*</span>
                    </label>
                    <div className="col-sm-8">
                      <Controller
                        control={control}
                        defaultValue={userInfoData.roles?.map((e) => e.feature_role_key)}
                        name="role"
                        render={({ field: { onChange, value, ref } }) => (
                          <Select
                            placeholder={'Buscar o asignar multiples roles'}
                            noOptionsMessage={() => 'Buscar'}
                            loadingMessage={() => 'Cargando...'}
                            isDisabled={isRoleSupervisor}
                            isClearable={true}
                            inputRef={ref}
                            value={featureRoles?.filter(
                              (c) => value.includes(c.value) && c.value !== 'role.supervisor'
                            )}
                            onChange={(val) => {
                              handleRoleChange(val)
                              onChange(val.map((c) => c.value))
                            }}
                            options={featureRoles}
                            isMulti
                          />
                        )}
                      />
                      <div className="invalid-feedback">{errors.role?.message}</div>
                    </div>
                  </div>

                  <div className="form-group row show-data-form">
                    <label htmlFor="inputCode" className="col-sm-3 col-form-label">
                      Número de rutero/Código: <span className="red">*</span>
                    </label>
                    <div className="col-sm-8">
                      <input
                        type="text"
                        id="inputCode"
                        className="form-control"
                        value={userInfoData.route_db_ref ?? ''}
                        disabled
                        style={{ backgroundColor: UI_COLOR_SURFACE_READONLY }}
                      />
                    </div>
                  </div>
                  <div className="form-group row show-data-form">
                    <label htmlFor="inputCode" className="col-sm-3 col-form-label">
                      Correo electrónico: <span className="red">*</span>
                    </label>
                    <div className="col-sm-8">
                      <input
                        name="email"
                        type="text"
                        {...register('email')}
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      />
                      <div className="invalid-feedback">{errors.email?.message}</div>
                    </div>
                  </div>

                  <div className="form-group row show-data-form">
                    <label htmlFor="inputCode" className="col-sm-3 col-form-label">
                      Código de bodega:
                    </label>
                    <div className="col-sm-8">
                      <Controller
                        control={control}
                        defaultValue={userInfoData.warehouse_uid}
                        name="warehouse"
                        render={({ field: { onChange, value, ref } }) => (
                          <Select
                            placeholder={'Buscar o asignar bodega'}
                            noOptionsMessage={() => 'Buscar'}
                            loadingMessage={() => 'Cargando...'}
                            isDisabled={false}
                            isClearable={true}
                            inputRef={ref}
                            value={warehouses?.find((c) => c.value === value)}
                            onChange={(val) => {
                              handleWarehouseChange(val)
                              onChange(val ? val.value : null)
                            }}
                            options={warehouses}
                          />
                        )}
                      />
                      <div className="invalid-feedback">{errors.warehouse?.message}</div>
                    </div>
                  </div>

                  <div className="form-group row">
                    <label htmlFor="inputCode" className="col-sm-3 col-form-label">
                      Número de teléfono (opcional):
                    </label>
                    <div className="col-sm-8">
                      <input
                        name="mobile"
                        type="number"
                        {...register('mobile')}
                        className={`form-control ${errors.mobile ? 'is-invalid' : ''}`}
                      />
                      <div className="invalid-feedback">{errors.mobile?.message}</div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
        {isFetching && (
          <div className="index-table">
            <div className="show-area-tab">
              <p style={{ textAlign: 'center' }}>Obteniendo datos ...</p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default UserInfoDetailTab
